import { Button, Form } from 'semantic-ui-react';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { EXPIRED_SKIP_MFA_DATE } from 'util/constants';
import { isEmpty } from 'lodash';
import { isStandardError } from '@upstart/harmony/lib/backend-validation-utils';
import { MfaStatusType } from '@upstart/harmony/lib/legos/constants/user';
import { MFAError } from '../../containers/Auth/error-handling';

import ErrorMessage from '../ErrorMessage';
import './OTPForm.scss';

interface Props {
  mfaToken: string;
  handleSubmit: Function;
  handleResendOTP: Function;
  skipMfaExplicitlyEnabled: boolean;
  handleSkipMFA: Function;
  handleBackToLogIn: Function;
  fetching: boolean;
  error: MFAError | null;
}

const isBeforeMfaDeadline =
  new Date().getTime() < new Date(EXPIRED_SKIP_MFA_DATE).getTime();

function getSkipMfaMessage() {
  const msg = 'Having trouble getting authenticated?';
  if (isBeforeMfaDeadline) {
    return `${msg} You can skip authentication until ${EXPIRED_SKIP_MFA_DATE}.`;
  }

  return msg;
}

const OTPForm: FunctionComponent<Props> = ({
  mfaToken,
  handleSubmit,
  handleResendOTP,
  handleBackToLogIn,
  skipMfaExplicitlyEnabled,
  handleSkipMFA,
  fetching,
  error: externalError,
}) => {
  const [authCode, setAuthCode] = useState('');
  const [error, setError] = useState<{ error: string } | null>(externalError);
  const [rememberDevice, setRememberDevice] = useState(false);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  const showSkipMFA = skipMfaExplicitlyEnabled || isBeforeMfaDeadline;

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^\d{0,6}$/.test(value)) {
      return;
    }

    setAuthCode(value);
  };

  const submitHandler = async () => {
    try {
      await handleSubmit({ rememberDevice, otp: authCode, mfaToken });
    } catch (e) {
      if (isStandardError(e)) {
        setError(e);
      } else {
        setError({ error: 'An unknown error has occured' });
      }
    }
  };

  if (
    externalError !== null &&
    !isEmpty(externalError) &&
    externalError.status === MfaStatusType.ACCOUNT_LOCKED
  ) {
    return (
      <>
        <ErrorMessage error={error} />
        <button
          className="link-btn"
          type="button"
          onClick={() => handleBackToLogIn()}
        >
          {`< Back to Log In`}
        </button>
      </>
    );
  }

  return (
    <>
      <h3 className="otp-form-header">
        Enter the 6-digit authentication code from the email or text message we
        just sent you.
      </h3>
      <Form
        className="otp-form"
        error={!!error}
        loading={fetching}
        onSubmit={submitHandler}
      >
        {error && <ErrorMessage error={error} />}
        <Form.Field className="auth-code">
          <label htmlFor="auth-code">Authentication Code</label>
          <input
            id="auth-code"
            type="input"
            value={authCode}
            onChange={onChangeHandler}
            placeholder="6-digit code..."
            required
          />
        </Form.Field>
        <Form.Field className="remember-device">
          <input
            id="remember-device"
            type="checkbox"
            onChange={() => setRememberDevice(!rememberDevice)}
            checked={rememberDevice}
          />
          <label htmlFor="remember-device">
            Remember this device for 30 days
          </label>
        </Form.Field>
        <Button color="blue" type="submit">
          Sign In
        </Button>
        <Button
          type="button"
          className="send-code-btn"
          onClick={() => handleResendOTP({ mfaToken })}
        >
          Send Code Again
        </Button>
      </Form>
      {showSkipMFA && (
        <div className="otp-form-skip-section">
          <p className="otp-form-message">{getSkipMfaMessage()}</p>
          <button
            className="link-btn"
            onClick={() => handleSkipMFA({ mfaToken })}
          >
            SKIP AUTHENTICATION
          </button>
        </div>
      )}
      <p className="otp-form-message">
        It may take up to five minutes to get the code. Please check your spam
        folder too. If you&apos;re still having problems logging in,{' '}
        <b>please contact our support team at 800-535-9749</b> Monday-Sunday
        9am-11pm EST.
      </p>
    </>
  );
};

export default OTPForm;
